import React from 'react';
import { Route } from '../../types/index';
import { formatDistance, formatMeters } from '../../utils/formatters';

export type ActivityKey =
  | 'skitouring'
  | 'rock_climbing'
  | 'mountain_climbing'
  | 'snow_ice_mixed'
  | 'ice_climbing'
  | 'snowshoeing'
  | 'hiking'
  | 'mountain_biking';

interface RouteTooltipProps {
  route: Route;
  tooltipTitle: string;
  activities: string;
  ratings: string[];
  selectedActivity?: ActivityKey;
}

// Styles extraits en constantes
const containerStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  color: 'white',
  padding: '14px 18px',
  borderRadius: '10px',
  fontSize: '13px',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  minWidth: 280,
  maxWidth: 350,
  wordWrap: 'break-word' as const,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
};

const titleStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 15,
  marginBottom: 10,
  color: '#fbbf24',
  wordWrap: 'break-word' as const,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.3,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 8,
};

const footerStyle: React.CSSProperties = {
  marginTop: 12,
  paddingTop: 8,
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  fontSize: 11,
  color: '#d1d5db',
  textAlign: 'center' as const,
};

const addRow = (label: string, content: React.ReactNode, color?: string) => (
  <div key={label} style={sectionStyle}>
    <strong style={{ color: color || '#ffffff' }}>{label}</strong> {content}
  </div>
);

const getPrimaryActivity = (route: Route, selectedActivity?: ActivityKey): ActivityKey | undefined => {
  if (selectedActivity && route.activities.includes(selectedActivity)) {
    return selectedActivity;
  }
  const first = route.activities[0] as ActivityKey | undefined;
  return first;
};

export const RouteTooltip: React.FC<RouteTooltipProps> = ({
  route,
  tooltipTitle,
  activities,
  ratings,
  selectedActivity,
}) => {
  const primaryActivity = getPrimaryActivity(route, selectedActivity);
  const detailRows: React.ReactNode[] = [];

  const addOptionalRow = (label: string, value: string | null | undefined, color?: string) => {
    if (value) {
      detailRows.push(addRow(label, value, color));
    }
  };

  const addNumericRow = (label: string, value: number | null | undefined, formatter = formatMeters, color?: string) => {
    detailRows.push(addRow(label, formatter(value), color));
  };

  if (primaryActivity === 'skitouring') {
    addOptionalRow('🗻 Cotation ski:', route.skiRating, '#fbbf24');
    addOptionalRow('⚠️ Exposition ski:', route.skiExposition, '#f97316');
    addNumericRow('⬆️ Dénivelé positif:', route.heightDiffUp, formatMeters, '#34d399');
    if (route.orientations && route.orientations.length > 0) {
      detailRows.push(addRow('🧭 Orientation:', route.orientations.join(', '), '#fb7185'));
    }
    addNumericRow('🧾 Altitudes:', route.elevationMin ?? null);
    addNumericRow('🧾 Altitude arrivée:', route.elevationMax ?? null);
  } else if (
    primaryActivity === 'mountain_climbing' ||
    primaryActivity === 'snow_ice_mixed' ||
    primaryActivity === 'ice_climbing'
  ) {
    addOptionalRow('🌐 Global rating:', route.globalRating, '#fbbf24');
    addOptionalRow('🛡️ Engagement:', route.engagementRating, '#f97316');
    addOptionalRow('⚡ Risque:', route.riskRating, '#ef4444');
    addOptionalRow('🧰 Équipement:', route.equipmentRating, '#facc15');
    addOptionalRow('🧭 Exposition:', route.expositionRockRating, '#60a5fa');
    addOptionalRow('🪨 Rock free:', route.rockFreeRating, '#a78bfa');
    addOptionalRow('🪨 Rock required:', route.rockRequiredRating, '#9333ea');
    addOptionalRow('🧱 AID:', route.aidRating, '#06b6d4');
  } else if (primaryActivity === 'rock_climbing') {
    addOptionalRow('🪨 Rock free:', route.rockFreeRating, '#a78bfa');
    addOptionalRow('🪨 Rock required:', route.rockRequiredRating, '#9333ea');
    addOptionalRow('🧱 AID:', route.aidRating, '#06b6d4');
    addOptionalRow('🛡️ Engagement:', route.engagementRating, '#f97316');
    addOptionalRow('⚡ Risque:', route.riskRating, '#ef4444');
    addOptionalRow('🧰 Équipement:', route.equipmentRating, '#facc15');
    addOptionalRow('🧭 Exposition:', route.expositionRockRating, '#60a5fa');
  } else if (primaryActivity === 'snowshoeing') {
    addOptionalRow('🎿 Cotation raquettes:', route.snowshoeRating, '#fbbf24');
    addNumericRow('⬆️ Dénivelé positif:', route.heightDiffUp, formatMeters, '#34d399');
    if (route.orientations && route.orientations.length > 0) {
      detailRows.push(addRow('🧭 Orientation:', route.orientations.join(', '), '#fb7185'));
    }
    addNumericRow('🧾 Altitudes:', route.elevationMin ?? null);
    addNumericRow('🧾 Altitude arrivée:', route.elevationMax ?? null);
  } else if (primaryActivity === 'hiking') {
    addOptionalRow('🥾 Cotation randonnée:', route.hikingRating, '#fbbf24');
    addNumericRow('⬆️ Dénivelé positif:', route.heightDiffUp, formatMeters, '#34d399');
    addNumericRow('🧾 Altitudes départ:', route.elevationMin ?? null);
    addNumericRow('🧾 Altitude arrivée:', route.elevationMax ?? null);
    addNumericRow('📏 Longueur:', route.routeLength ?? null, formatDistance, '#f97316');
  } else if (primaryActivity === 'mountain_biking') {
    addNumericRow('📏 Longueur:', route.routeLength ?? null, formatDistance, '#f97316');
    addNumericRow('⬆️ Dénivelé positif:', route.heightDiffUp, formatMeters, '#34d399');
    addNumericRow('🧾 Altitudes départ:', route.elevationMin ?? null);
    addNumericRow('🧾 Altitude arrivée:', route.elevationMax ?? null);
  } else {
    // Fallback: show basic height/orientation info
    addNumericRow('⬆️ Dénivelé positif:', route.heightDiffUp, formatMeters, '#34d399');
    addNumericRow('🧾 Altitudes départ:', route.elevationMin ?? null);
    addNumericRow('🧾 Altitude arrivée:', route.elevationMax ?? null);
    if (route.orientations && route.orientations.length > 0) {
      detailRows.push(addRow('🧭 Orientation:', route.orientations.join(', '), '#fb7185'));
    }
  }

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>{tooltipTitle}</div>

      <div style={sectionStyle}>
        <strong style={{ color: '#a78bfa' }}>🎯 Activités:</strong> {activities}
      </div>

      {ratings.length > 0 && (
        <div style={sectionStyle}>
          <strong style={{ color: '#34d399' }}>📊 Cotations:</strong> {ratings.join(' • ')}
        </div>
      )}

      {detailRows}

      {route.publicTransportationRating && (
        <div style={sectionStyle}>
          <strong style={{ color: '#06b6d4' }}>🚌 Transport:</strong> {route.publicTransportationRating}
        </div>
      )}

      <div style={footerStyle}>🔗 Cliquez pour ouvrir en arrière-plan</div>
    </div>
  );
};

export default RouteTooltip;
